import type { TimelineBlock, TimelineGap } from "../../api/types";
import { LIVE_EDGE_FLUSH_MS, type Placed, type View, place, timeToX } from "../geometry";
import { type Bands, theme } from "../theme";

export function drawGaps(
  ctx: CanvasRenderingContext2D,
  view: View,
  gaps: readonly TimelineGap[],
  nowMs: number,
  b: Bands,
): void {
  // A gap is missing audio, not silence: nothing was recorded there, or what was has aged out
  // with no export behind it. A flat grey wash -- the stripes belong to drawVoid, which is the
  // only place on this canvas they are unambiguous.
  for (const gap of place(view, gaps, 1)) {
    // Except at the leading edge, where the hole is the time map catching up rather than
    // anything lost: the recorder is writing that stretch right now. Hatched, it lays a dead
    // zone over the block that is on air and over the now marker itself. Past the flush slack
    // the recording really has stopped, and then it is drawn from where it stopped.
    const reachesTheClock = Date.parse(gap.item.end) >= nowMs;
    const lag = nowMs - Date.parse(gap.item.start);
    const from =
      reachesTheClock && lag <= LIVE_EDGE_FLUSH_MS
        ? Math.max(gap.x0, timeToX(view, nowMs))
        : gap.x0;

    const x0 = Math.max(0, from);
    const x1 = Math.min(view.width, gap.x1);
    if (x1 <= x0) continue;

    ctx.fillStyle = theme.gapFill;
    ctx.fillRect(x0, b.contentTop, x1 - x0, b.contentBottom - b.contentTop);
  }
}

/**
 * Audio the permanent archive holds: an exported show, end to end.
 *
 * A flat wash and a hard top rule, amber rather than the grey of a gap: that wash means
 * "nothing to play here" and there is a show over this that plays perfectly well. The wash is
 * the positive claim -- these seconds exist on the archive disk and survive whatever the
 * buffer does.
 *
 * Scrubbing the bare timeline across the span is the buffer's question, not this one, and
 * where the buffer cannot, the stretch is a gap and gets the grey.
 *
 * No live-edge special case, unlike the gaps: a span can only be archived once the recorder
 * has moved past it and the export has run.
 */
export function drawArchived(
  ctx: CanvasRenderingContext2D,
  view: View,
  spans: readonly TimelineGap[],
  b: Bands,
): void {
  for (const span of place(view, spans, 1)) {
    const x0 = Math.max(0, span.x0);
    const x1 = Math.min(view.width, span.x1);
    if (x1 <= x0) continue;
    ctx.fillStyle = theme.archivedFill;
    ctx.fillRect(x0, b.contentTop, x1 - x0, b.contentBottom - b.contentTop);
    ctx.fillStyle = theme.archivedEdge;
    ctx.fillRect(x0, b.contentTop, x1 - x0, 1);
  }
}

/**
 * How much of each show this listener has heard: a violet rule under every playable show
 * block, with the stretches they have played laid over it in grey.
 *
 * In the padding between the block and the lane below rather than inside it, for the same
 * reason the archived band is a rule above rather than a tint within: the block's own fill is
 * already saying what the block is, and a second claim painted over it would be read as a
 * change to the first.
 *
 * The base follows the placed blocks, so an on-air show's rule grows with the clock and an
 * edge being dragged takes its rule with it. Three things go over it in grey: what the server
 * has recorded, what this page has heard since it loaded and not yet had back, and the
 * stretch playing right now, which reaches the cursor and is taken off again if the listener
 * seeks away before it lasts. All three run through the same clip against the placed blocks,
 * because the last two are raw wall clock off the playhead -- a listener who plays on past
 * the end of a show is still hearing something, and grey where no violet was laid would be a
 * rule under nothing.
 */
export function drawListened(
  ctx: CanvasRenderingContext2D,
  view: View,
  shows: readonly Placed<TimelineBlock>[],
  heard: readonly TimelineGap[],
  heardNow: readonly TimelineGap[],
  live: readonly [number, number] | null,
  b: Bands,
): void {
  const top = b.showBottom + 1;
  // Fills the rest of `layout.rowPad`: one pixel clear of the block above, flush against the
  // lane below. Three rather than two, because a two-pixel rule is thinner than the eye can
  // read a colour off, and heard and unheard are told apart by colour alone.
  const height = 3;
  // Expired shows are left out entirely: with the audio gone there is nothing to play, so a
  // violet rule under the block is a task nobody can finish, and the grey is clipped to the
  // same list so nothing is laid over a show that no longer has a tracker.
  const tracked = shows.filter((show) => show.item.playable);
  ctx.fillStyle = theme.unheardFill;
  for (const show of tracked) {
    const x0 = Math.max(0, show.x0);
    const x1 = Math.min(view.width, show.x1);
    if (x1 > x0) ctx.fillRect(x0, top, x1 - x0, height);
  }
  ctx.fillStyle = theme.heardFill;
  const over = (from: number, to: number) => {
    for (const show of tracked) {
      const x0 = Math.max(from, show.x0, 0);
      const x1 = Math.min(to, show.x1, view.width);
      if (x1 > x0) ctx.fillRect(x0, top, x1 - x0, height);
    }
  };
  for (const span of place(view, heard, 1)) over(span.x0, span.x1);
  for (const span of place(view, heardNow, 1)) over(span.x0, span.x1);
  if (live) over(timeToX(view, live[0]), timeToX(view, live[1]));
}

/**
 * The live delay: from the newest playable instant to the clock.
 *
 * A flat wash and a hard line at its left edge. The line is the point of the whole marking --
 * it is the wall a pan and a click both stop at -- and it is drawn whatever the zoom, because
 * at anything wider than a few minutes on screen the band itself is thinner than a pixel and a
 * wall nobody can see is a wall that reads as a bug.
 */
export function drawLiveDelay(
  ctx: CanvasRenderingContext2D,
  view: View,
  fromMs: number,
  nowMs: number,
  b: Bands,
): void {
  const wall = timeToX(view, fromMs);
  const x0 = Math.max(0, wall);
  const x1 = Math.min(view.width, timeToX(view, nowMs));
  if (x1 > x0) {
    ctx.fillStyle = theme.liveDelayFill;
    ctx.fillRect(x0, b.contentTop, x1 - x0, b.contentBottom - b.contentTop);
  }

  if (wall < 0 || wall > view.width) return;
  ctx.strokeStyle = theme.liveDelayEdge;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(Math.round(wall) + 0.5, b.contentTop);
  ctx.lineTo(Math.round(wall) + 0.5, b.contentBottom);
  ctx.stroke();
}

/**
 * The two stretches that are not time this archive can hold: before its first row, and after
 * the clock.
 *
 * The only hatch left on the canvas. It used to mark a hole in the recording, where it said
 * "nothing to play" over stretches the archive is perfectly honest about -- an expired show is
 * grey and still a block, and stripes across it read as a fault.
 *
 * Filled with the well before the stripes go on, and painted last of the content markings: a
 * gap band runs to the end of the fetched window and a window at the live edge reaches into the
 * future, so a grey wash under the stripes would show a marking about the record in a place
 * where there is no record. Nothing legitimate is buried -- every block is placed against
 * `liveEndMs`, which stops at the clock, and no row starts before `archiveStartMs`.
 */
export function drawVoid(
  ctx: CanvasRenderingContext2D,
  view: View,
  archiveStartMs: number | null,
  nowMs: number,
  b: Bands,
): void {
  const spans: [number, number][] = [[timeToX(view, nowMs), view.width]];
  if (archiveStartMs !== null) spans.unshift([0, timeToX(view, archiveStartMs)]);
  const height = b.contentBottom - b.contentTop;
  for (const [from, to] of spans) {
    const x0 = Math.max(0, from);
    const x1 = Math.min(view.width, to);
    if (x1 <= x0) continue;
    ctx.fillStyle = theme.background;
    ctx.fillRect(x0, b.contentTop, x1 - x0, height);

    ctx.save();
    ctx.beginPath();
    ctx.rect(x0, b.contentTop, x1 - x0, height);
    ctx.clip();
    ctx.strokeStyle = theme.voidHatch;
    ctx.lineWidth = 1;
    // Swept across the whole viewport and clipped, rather than from the band's own left edge:
    // the phase is then a function of the canvas and not of where the band happens to fall, so
    // the stripes stand still under a pan instead of crawling, and the two bands share one grid.
    for (let x = x0 - height; x < x1 + height; x += 7) {
      ctx.beginPath();
      ctx.moveTo(x, b.contentBottom);
      ctx.lineTo(x + height, b.contentTop);
      ctx.stroke();
    }
    ctx.restore();
  }
}

/**
 * Where a dragged edge will land if released now.
 *
 * Drawn because the snap is otherwise invisible until it has already happened: without a
 * mark, a boundary that jumps four pixels on release looks like a mis-drag rather than the
 * feature that makes second-precision reachable at seven-day zoom.
 */
export function drawSnapMark(
  ctx: CanvasRenderingContext2D,
  view: View,
  ms: number,
  b: Bands,
): void {
  const x = timeToX(view, ms);
  if (x < -4 || x > view.width + 4) return;
  ctx.save();
  ctx.strokeStyle = theme.label;
  ctx.lineWidth = 1;
  ctx.setLineDash([2, 3]);
  ctx.beginPath();
  ctx.moveTo(Math.round(x) + 0.5, b.showTop);
  ctx.lineTo(Math.round(x) + 0.5, b.showBottom);
  ctx.stroke();
  ctx.restore();
}

/**
 * The playhead, at the instant it denotes.
 *
 * Placed through `timeToX` rather than at the middle of the canvas. The two agree while the
 * view is following playback, which is most of the time and is why the fixed pixel survived
 * -- but the moment following stops, the centre is just wherever the view was left, and a
 * bar nailed to it slid onto a different instant with every zoom notch.
 */
export function drawPlayhead(
  ctx: CanvasRenderingContext2D,
  view: View,
  playheadMs: number,
  height: number,
): void {
  const x = Math.round(timeToX(view, playheadMs)) + 0.5;
  if (x < 0 || x > view.width) return;
  ctx.fillStyle = theme.playheadGlow;
  ctx.fillRect(x - 3, 0, 6, height);
  ctx.strokeStyle = theme.playhead;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, 0);
  ctx.lineTo(x, height);
  ctx.stroke();

  ctx.fillStyle = theme.playhead;
  ctx.beginPath();
  ctx.moveTo(x - 5, 0);
  ctx.lineTo(x + 5, 0);
  ctx.lineTo(x, 7);
  ctx.closePath();
  ctx.fill();
}

export function drawNowMarker(
  ctx: CanvasRenderingContext2D,
  view: View,
  nowMs: number,
  height: number,
): void {
  const x = timeToX(view, nowMs);
  if (x < 0 || x > view.width) return;
  ctx.save();
  ctx.strokeStyle = theme.playhead;
  ctx.globalAlpha = 0.35;
  ctx.setLineDash([3, 4]);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(Math.round(x) + 0.5, 0);
  ctx.lineTo(Math.round(x) + 0.5, height);
  ctx.stroke();
  ctx.restore();
}
